import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import moment, { Moment } from 'moment';
import analyticsService, { ActivityTimelineData } from '../../../../../services/analytics/AnalyticsService';
import { ActivityTypeColors } from '../../../../../config/constants';
import PortalCard from '../../../../../components/PortalCard';

export type DateRange = [Moment, Moment];

interface AuthChartProps {
    dateRange: DateRange;
}

const AuthChart: React.FC<AuthChartProps> = ({ dateRange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [chartData, setChartData] = useState<ActivityTimelineData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!dateRange[0] || !dateRange[1]) return;

            setLoading(true);
            try {
                const result = await analyticsService.getActivityTimeline(
                    dateRange[0].toISOString(),
                    dateRange[1].toISOString()
                );

                if (result.success && result.data) {
                    setChartData(result.data);
                }
            } catch (error) {
                console.error('Error fetching auth data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange]);

    const formatDateLabel = (dateStr: string) => {
        const date = moment(dateStr);
        const daysDiff = dateRange[1] && dateRange[0] ? dateRange[1].diff(dateRange[0], 'days') : 7;
        
        if (daysDiff <= 1) {
            return date.format('HH:mm');
        } else if (daysDiff <= 31) {
            return date.format('MMM DD');
        } else {
            return date.format('MMM YYYY');
        }
    };

    const xAxis = chartData.map(item => formatDateLabel(item.timePeriod));
    const signInValues = chartData.map(item => item.signIns || 0);
    const signOutValues = chartData.map(item => item.signOuts || 0);
    const signUpValues = chartData.map(item => item.signUps || 0);

    return (
        <PortalCard
            sx={{
                p: isMobile ? 2 : 1.5,
                pb: isMobile ? 3 : 1.5,
                mb: isMobile ? 2 : 0,
                height: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible'
            }}
        >
            <Box sx={{ mb: isMobile ? 1.5 : 1 }}>
                <Typography variant='subtitle1' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.25 }}>
                    Authentication Activity
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.75rem' }}>
                    User sign-ins, sign-outs, and registrations
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: isMobile ? 220 : 120, width: '100%', position: 'relative', overflow: 'visible' }}>
                {chartData.length > 0 ? (
                    <LineChart
                        xAxis={[{ 
                            data: xAxis, 
                            scaleType: 'point',
                            tickLabelStyle: {
                                fontSize: isMobile ? 10 : 11,
                            }
                        }]}
                        series={[
                            {
                                data: signInValues,
                                label: 'Sign Ins',
                                color: ActivityTypeColors.sign_in,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: signOutValues,
                                label: 'Sign Outs',
                                color: ActivityTypeColors.sign_out,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: signUpValues,
                                label: 'Sign Ups',
                                color: ActivityTypeColors.sign_up,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                        ]}
                        height={isMobile ? 240 : 150}
                        margin={{ 
                            top: 5, 
                            right: isMobile ? 15 : 20, 
                            bottom: isMobile ? 60 : 45, 
                            left: isMobile ? 30 : 35 
                        }}
                        slotProps={{
                            legend: {
                                position: {
                                    vertical: 'bottom',
                                    horizontal: 'middle'
                                },
                                padding: { bottom: isMobile ? 5 : 0, top: isMobile ? 10 : 5 },
                                itemGap: isMobile ? 12 : 12,
                                itemMarkWidth: isMobile ? 10 : 8,
                                itemMarkHeight: isMobile ? 10 : 8,
                                labelStyle: {
                                    fontSize: isMobile ? 11 : 11,
                                }
                            },
                        }}
                        sx={{
                            '.MuiLineElement-root': { strokeWidth: isMobile ? 2 : 2 },
                            '.MuiMarkElement-root': { r: isMobile ? 3 : 3 },
                            '& .MuiChartsAxis-tickLabel': {
                                fontSize: isMobile ? '0.7rem' : '0.7rem',
                                fill: theme.palette.text.secondary
                            },
                            '& .MuiChartsLegend-label': {
                                fontSize: isMobile ? '0.75rem' : '0.75rem',
                            }
                        }}
                    />
                ) : (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant='body2' color='text.secondary'>
                            No authentication data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default AuthChart;

